"use client"

import { type ComponentProps, Fragment, type Key, type ReactNode, useState } from "react"
import { Note } from "~/components/legacy/common/note"
import { Stack } from "~/components/legacy/common/stack"

type ShowMoreProps<T> = ComponentProps<typeof Stack> & {
  items: T[]
  limit?: number
  buffer?: number
  showMoreType?: "link" | "text" | "none"
  getKey?: (item: T) => Key
  renderItem: (item: T) => ReactNode
}

export const ShowMore = <T,>({
  items,
  limit = 4,
  buffer = 1,
  showMoreType = "link",
  getKey,
  renderItem,
  ...props
}: ShowMoreProps<T>) => {
  const [showAll, setShowAll] = useState(false)

  const shouldShowAll = showAll || items.length <= limit + buffer
  const visibleItems = shouldShowAll ? items : items.slice(0, limit)
  const hiddenCount = items.length - limit

  if (!items.length) return null

  return (
    <Stack {...props}>
      {visibleItems.map(item => (
        <Fragment key={getKey?.(item) ?? String(item)}>{renderItem(item)}</Fragment>
      ))}

      {!shouldShowAll && hiddenCount > 0 && (
        <>
          {showMoreType === "link" && (
            <button
              type="button"
              className="font-medium text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setShowAll(true)}
            >
              +{hiddenCount} more
            </button>
          )}

          {showMoreType === "text" && <Note className="text-xs">+{hiddenCount} more</Note>}
        </>
      )}
    </Stack>
  )
}
